import React, { useState, useEffect } from 'react';
import Sidebar from './dashboard/Sidebar';
import StatCards from './dashboard/StatCards';
import FlashSection from './dashboard/FlashSection';
import PartnersSection from './dashboard/PartnersSection';
import VideosSection from './dashboard/VideosSection';
import ProgrammeSection from './dashboard/ProgrammeSection';
import MediaModal from './MediaModal';
import PartnerModal from './PartnerModal';
import { 
  getAllVideos, createVideo, deleteVideo, 
  getAllPartners, createPartner, updatePartner, deletePartner, 
  getCurrentFlash, updateFlash 
} from '../services/api';

const Dashboard = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [videos, setVideos] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showPartnerModal, setShowPartnerModal] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingPartner, setIsSavingPartner] = useState(false);
  const [flashInfos, setFlashInfos] = useState(["", "", ""]); 
  const [flashStatus, setFlashStatus] = useState("");

  useEffect(() => {
    fetchVideos();
    fetchPartners();
    fetchCurrentFlash();
  }, []);

  // --- LOGIQUE VIDEOS ---
  const fetchVideos = async () => {
    setLoading(true);
    try { const res = await getAllVideos(); setVideos(res.data); } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const handleSaveMedia = async (videoData) => {
    setIsSaving(true);
    try { await createVideo(videoData); setShowModal(false); fetchVideos(); } catch (err) { alert("Erreur"); } finally { setIsSaving(false); }
  };

  const handleDeleteVideo = async (id) => {
    if (window.confirm("❗ Supprimer définitivement cette vidéo ?")) {
      try { await deleteVideo(id); setVideos(videos.filter(v => v.id !== id)); } catch (err) { console.error(err); }
    }
  };

  // --- LOGIQUE PARTENAIRES ---
  const fetchPartners = async () => {
    setLoading(true);
    try { const res = await getAllPartners(); setPartners(res.data); } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const handleSavePartner = async (formData) => {
    setIsSavingPartner(true);
    try {
      if (editingPartner) { await updatePartner(editingPartner.id, formData); } 
      else { await createPartner(formData); }
      setShowPartnerModal(false); setEditingPartner(null); fetchPartners();
    } catch (err) { alert("Erreur"); } finally { setIsSavingPartner(false); }
  };

  const handleDeletePartner = async (id) => {
    if (window.confirm("❗ Supprimer ce partenaire ?")) {
      try { await deletePartner(id); setPartners(partners.filter(p => p.id !== id)); } catch (err) { console.error(err); }
    }
  };

  // --- LOGIQUE FLASH INFO ---
  const fetchCurrentFlash = async () => {
    try {
      const res = await getCurrentFlash();
      if (res.data && res.data.message) {
        const splitMessages = res.data.message.split(' • ');
        setFlashInfos(splitMessages.length > 0 ? splitMessages : ["", "", ""]);
      }
    } catch (err) { console.error(err); }
  };

  const handleUpdateFlash = async (e) => {
    e.preventDefault();
    setFlashStatus("Mise à jour...");
    const combinedMessage = flashInfos.filter(msg => msg.trim() !== "").join(' • ');
    try {
      await updateFlash({ message: combinedMessage, user_name: user.username || 'Admin' });
      setFlashStatus("✅ Flash Info mis à jour !");
      setTimeout(() => setFlashStatus(""), 3000);
    } catch (err) { setFlashStatus("❌ Erreur serveur"); }
  };

  return (
    <div className="d-flex flex-column flex-md-row min-vh-100 bg-dark text-light overflow-x-hidden">
        {/* LOGO MOBILE UNIQUEMENT */}
        <div className="d-md-none text-center mb-4 pt-2">
          <h2 className="fw-black italic tracking-tighter text-uppercase mb-0 text-white" style={{ fontSize: '24px' }}>
            FRÉQUENCE<span className="text-danger">974</span>
          </h2>
          <small className="text-info text-uppercase fw-bold" style={{ fontSize: '10px', letterSpacing: '2px' }}>Studio Digital</small>
        </div>
        
      {/* ESPACE AU DESSUS DU SIDEBAR (pt-4 pour desktop, pt-2 pour mobile) */}
      <div className="pt-0 pt-md-4">
        <Sidebar user={user} onLogout={onLogout} activeTab={activeTab} setActiveTab={setActiveTab} />
      </div>

      <main className="flex-grow-1 p-3 p-md-5 pb-5 mb-5" style={{ background: '#0a0f1a', minWidth: 0, paddingBottom: '100px' }}>
        
        <header className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-end mb-5 gap-3">
          <div>
            <h6 className="text-info fw-black text-uppercase tracking-widest mb-1">Espace Administration</h6>
            <h1 className="fw-black text-uppercase m-0 italic tracking-tighter display-6 text-white">
              {activeTab === 'overview' && "Console Centrale"}
              {activeTab === 'flash' && "Flash Information"}
              {activeTab === 'videos' && "Régie Vidéo"}
              {activeTab === 'partners' && "Réseau Partenaires"}
              {activeTab === 'programme' && "Grille des Programmes"}
            </h1>
          </div>
          
          <div className="d-flex flex-column flex-md-row gap-3 align-items-center w-100 w-md-auto">
            {/* BOUTON RETOUR SUR LE SITE - Mis à jour pour visibilité totale */}
            <a 
              href="/" 
              className="btn btn-outline-info rounded-pill px-4 py-2 fw-black text-uppercase tracking-widest btn-sm shadow-sm w-100 w-md-auto"
            >
              <i className="bi bi-arrow-left me-2"></i>
              Retour sur le site
            </a>

            <div className="badge bg-secondary bg-opacity-25 border border-secondary p-3 rounded-4 shadow-sm fw-bold w-100 w-md-auto text-center">
              {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
            </div>
          </div>
        </header>

        <div className="fade-in">
          {activeTab === 'overview' && <StatCards videoCount={videos.length} partnerCount={partners.length} />}
          {activeTab === 'flash' && <FlashSection flashInfos={flashInfos} setFlashInfos={setFlashInfos} handleUpdateFlash={handleUpdateFlash} flashStatus={flashStatus} user={user} />}
          {activeTab === 'partners' && <PartnersSection partners={partners} loading={loading} onEdit={(p) => { setEditingPartner(p); setShowPartnerModal(true); }} onDelete={handleDeletePartner} onCreate={() => { setEditingPartner(null); setShowPartnerModal(true); }} />}
          {activeTab === 'videos' && <VideosSection videos={videos} loading={loading} onDelete={handleDeleteVideo} onCreate={() => setShowModal(true)} />}
          {activeTab === 'programme' && <ProgrammeSection />}
        </div>
      </main>

      <MediaModal show={showModal} onClose={() => setShowModal(false)} onSave={handleSaveMedia} loading={isSaving} />
      <PartnerModal show={showPartnerModal} onClose={() => { setShowPartnerModal(false); setEditingPartner(null); }} onSave={handleSavePartner} loading={isSavingPartner} initialData={editingPartner} />

      <style>{`
        .fw-black { font-weight: 900; }
        .italic { font-style: italic; }
        .tracking-tighter { letter-spacing: -1.5px; }
        .tracking-widest { letter-spacing: 2px; }
        .fade-in { animation: fadeIn 0.4s ease-out; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
};

export default Dashboard;